import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Header from '../../components/ui/Header';
import FloatingActionButton from '../../components/ui/FloatingActionButton';
import { useToast } from '../../components/ui/Toast';
import EventDetailsForm from './components/EventDetailsForm';
import GuestSelectionPanel from './components/GuestSelectionPanel';
import DietarySummaryPanel from './components/DietarySummaryPanel';
import CalendarIntegration from './components/CalendarIntegration';
import RepeatWindowSettings from './components/RepeatWindowSettings';
import PostDinnerFeedback from './components/PostDinnerFeedback';

const DinnerPlanning = () => {
  const navigate = useNavigate();
  const { showSuccess, showError, showWarning } = useToast();
  
  // Current user mock data
  const [currentUser] = useState({
    id: 1,
    name: "Alex Thompson",
    email: "alex.thompson@email.com"
  });

  // Form state
  const [eventData, setEventData] = useState({
    title: '',
    date: '',
    time: '',
    location: '',
    occasionType: '',
    duration: '',
    notes: '',
    sendInvitations: true,
    enableReminders: true,
    allowSuggestions: false,
    hasKidsAttending: false,
    expectedKidsCount: 0,
    kidsAgeRange: '',
    giftCoordination: true
  });

  const [selectedGuests, setSelectedGuests] = useState([]);
  const [advancedSettings, setAdvancedSettings] = useState({
    repeatWindow: '3',
    globalRepeatWindow: false,
    includeSimilarDishes: false,
    reminders: ['7', '2', '1'],
    enableAISuggestions: true,
    autoShoppingList: true,
    considerSeasonal: false,
    budgetConscious: false,
    shareMenuAdvance: false,
    collectFeedback: true,
    saveEventPhotos: false
  });

  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('details');
  const [showPostDinnerFeedback, setShowPostDinnerFeedback] = useState(false);

  // Auto-save functionality
  useEffect(() => {
    const autoSaveTimer = setTimeout(() => {
      if (eventData?.title || eventData?.date || selectedGuests?.length > 0) {
        localStorage.setItem('dinnerPlanningDraft', JSON.stringify({
          eventData,
          selectedGuests,
          advancedSettings,
          lastSaved: new Date()?.toISOString()
        }));
      }
    }, 2000);

    return () => clearTimeout(autoSaveTimer);
  }, [eventData, selectedGuests, advancedSettings]);

  // Load draft on component mount
  useEffect(() => {
    const savedDraft = localStorage.getItem('dinnerPlanningDraft');
    if (savedDraft) {
      try {
        const draft = JSON.parse(savedDraft);
        const lastSaved = new Date(draft.lastSaved);
        const hoursSinceLastSave = (new Date() - lastSaved) / (1000 * 60 * 60);
        
        if (hoursSinceLastSave < 24) {
          setEventData(draft?.eventData || {});
          setSelectedGuests(draft?.selectedGuests || []);
          setAdvancedSettings(draft?.advancedSettings || advancedSettings);
          showSuccess('Draft restored from previous session');
        }
      } catch (error) {
        console.error('Error loading draft:', error);
      }
    }
  }, []);

  const validateForm = () => {
    const newErrors = {};

    if (!eventData?.title?.trim()) {
      newErrors.title = 'Event title is required';
    }

    if (!eventData?.date) {
      newErrors.date = 'Event date is required';
    } else {
      const eventDate = new Date(eventData.date);
      const today = new Date();
      today?.setHours(0, 0, 0, 0);
      
      if (eventDate < today) {
        newErrors.date = 'Event date cannot be in the past';
      }
    }

    if (!eventData?.time) {
      newErrors.time = 'Event time is required';
    }

    if (!eventData?.location?.trim()) {
      newErrors.location = 'Event location is required';
    }

    if (selectedGuests?.length === 0) {
      newErrors.guests = 'At least one guest must be selected';
    }

    if (selectedGuests?.length > 20) {
      newErrors.guests = 'Maximum 20 guests allowed per event';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleGuestToggle = (guestId) => {
    setSelectedGuests(prev => 
      prev?.includes(guestId)
        ? prev?.filter(id => id !== guestId)
        : [...prev, guestId]
    );
  };

  const handleFamilyToggle = (familyId) => {
    // Mock family members data
    const familyMembers = {
      'family_1': [1, 2],
      'family_2': [4, 5]
    };

    const members = familyMembers?.[familyId] || [];
    const allSelected = members?.every(id => selectedGuests?.includes(id));

    if (allSelected) {
      setSelectedGuests(prev => prev?.filter(id => !members?.includes(id)));
    } else {
      setSelectedGuests(prev => [...new Set([...prev, ...members])]);
    }
  };

  const handleCalendarSync = (syncSettings) => {
    showSuccess('Event synced to calendar successfully');
  };

  const handleSaveEvent = async () => {
    if (!validateForm()) {
      showError('Please fix the errors before saving');
      return;
    }

    setIsLoading(true);
    
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // Clear draft
      localStorage.removeItem('dinnerPlanningDraft');
      
      showSuccess('Dinner event created successfully!');
      
      // Ask if they want to collect feedback (for completed events)
      if (advancedSettings?.collectFeedback) {
        setTimeout(() => {
          setShowPostDinnerFeedback(true);
        }, 1000);
      }
      
    } catch (error) {
      showError('Failed to create event. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePostDinnerFeedbackSave = (feedbackData) => {
    console.log('Saving post-dinner feedback:', feedbackData);
    // In a real app, this would update guest profiles with new preferences
    showSuccess('Guest preferences updated successfully!');
  };

  const handleQuickPlan = () => {
    // Quick plan with minimal required fields
    const quickEventData = {
      title: `Dinner Party - ${new Date()?.toLocaleDateString()}`,
      date: new Date()?.toISOString()?.split('T')?.[0],
      time: '19:00',
      location: 'My Home',
      occasionType: 'casual',
      duration: '3'
    };
    
    setEventData(prev => ({ ...prev, ...quickEventData }));
    setActiveTab('guests');
    showSuccess('Quick event template applied');
  };

  const handleFloatingAction = (actionType) => {
    switch (actionType) {
      case 'new-event':
        // Reset form for new event
        setEventData({});
        setSelectedGuests([]);
        setErrors({});
        setActiveTab('details');
        showSuccess('New event form ready');
        break;
      case 'quick-plan':
        handleQuickPlan();
        break;
      case 'add-guest': navigate('/guest-management');
        break;
      case 'collect-feedback':
        setShowPostDinnerFeedback(true);
        break;
      default:
        break;
    }
  };

  const tabs = [
    { id: 'details', label: 'Event Details', icon: 'Calendar' },
    { id: 'guests', label: 'Select Guests', icon: 'Users' },
    { id: 'dietary', label: 'Dietary Summary', icon: 'Shield' },
    { id: 'calendar', label: 'Calendar Sync', icon: 'Sync' },
    { id: 'settings', label: 'Advanced', icon: 'Settings' }
  ];

  const canProceedToNext = () => {
    switch (activeTab) {
      case 'details':
        return eventData?.title && eventData?.date && eventData?.time && eventData?.location;
      case 'guests':
        return selectedGuests?.length > 0;
      default:
        return true;
    }
  };

  const getNextTab = () => {
    const currentIndex = tabs?.findIndex(tab => tab?.id === activeTab);
    return currentIndex < tabs?.length - 1 ? tabs?.[currentIndex + 1]?.id : null;
  };

  const getPreviousTab = () => {
    const currentIndex = tabs?.findIndex(tab => tab?.id === activeTab);
    return currentIndex > 0 ? tabs?.[currentIndex - 1]?.id : null;
  };

  return (
    <div className="min-h-screen bg-background">
      <Header currentUser={currentUser} onLogout={() => navigate('/login')} />
      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Header Section */}
          <div className="mb-8">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-foreground">Plan Your Dinner Event</h1>
                <p className="text-muted-foreground mt-2">
                  Create memorable dinner experiences with intelligent planning and gift coordination
                </p>
              </div>
              
              <div className="hidden lg:flex items-center space-x-3">
                <Button
                  variant="outline"
                  onClick={handleQuickPlan}
                  iconName="Zap"
                  iconPosition="left"
                >
                  Quick Plan
                </Button>

                <Button
                  variant="outline"
                  onClick={() => setShowPostDinnerFeedback(true)}
                  iconName="MessageSquare"
                  iconPosition="left"
                >
                  Collect Feedback
                </Button>
                
                <Button
                  variant="default"
                  onClick={handleSaveEvent}
                  loading={isLoading}
                  disabled={!canProceedToNext() || selectedGuests?.length === 0}
                  iconName="Save"
                  iconPosition="left"
                >
                  Save Event
                </Button>
              </div>
            </div>

            {/* Progress Indicator */}
            <div className="mt-6">
              <div className="flex items-center space-x-4 overflow-x-auto pb-2">
                {tabs?.map((tab, index) => {
                  const isActive = tab?.id === activeTab;
                  const isCompleted = tabs?.findIndex(t => t?.id === activeTab) > index;
                  
                  return (
                    <button
                      key={tab?.id}
                      onClick={() => setActiveTab(tab?.id)}
                      className={`flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium transition-warm whitespace-nowrap ${
                        isActive
                          ? 'bg-primary text-primary-foreground'
                          : isCompleted
                          ? 'bg-success/10 text-success-foreground'
                          : 'bg-muted text-muted-foreground hover:bg-muted/80'
                      }`}
                    >
                      <Icon 
                        name={isCompleted ? 'Check' : tab?.icon} 
                        size={16} 
                      />
                      <span>{tab?.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Primary Content */}
            <div className="lg:col-span-2 space-y-6">
              {activeTab === 'details' && (
                <EventDetailsForm
                  formData={eventData}
                  onChange={setEventData}
                  errors={errors}
                />
              )}

              {activeTab === 'guests' && (
                <GuestSelectionPanel
                  selectedGuests={selectedGuests}
                  onGuestToggle={handleGuestToggle}
                  onFamilyToggle={handleFamilyToggle}
                />
              )}

              {activeTab === 'dietary' && (
                <DietarySummaryPanel selectedGuests={selectedGuests} />
              )}

              {activeTab === 'calendar' && (
                <CalendarIntegration
                  eventData={eventData}
                  onCalendarSync={handleCalendarSync}
                />
              )}

              {activeTab === 'settings' && (
                <RepeatWindowSettings
                  settings={advancedSettings}
                  onChange={setAdvancedSettings}
                />
              )}

              {/* Mid-Page Call to Action Button */}
              <div className="flex justify-center py-8">
                <Button
                  variant="default"
                  size="lg"
                  onClick={handleSaveEvent}
                  loading={isLoading}
                  disabled={!canProceedToNext() || selectedGuests?.length === 0}
                  iconName="Save"
                  iconPosition="left"
                  className="rounded-full px-8 py-4 text-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
                >
                  Save Dinner Event
                </Button>
              </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Quick Stats */}
              <div className="bg-card rounded-lg border border-border p-6">
                <h3 className="text-lg font-semibold text-foreground mb-4">Event Summary</h3>
                
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Event Date</span>
                    <span className="font-medium text-foreground">
                      {eventData?.date ? new Date(eventData.date)?.toLocaleDateString() : 'Not set'}
                    </span>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Guests</span>
                    <span className="font-medium text-foreground">{selectedGuests?.length}</span>
                  </div>

                  {/* Gift Summary */}
                  {selectedGuests?.length > 0 && (
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">Guests with Gifts</span>
                      <span className="font-medium text-foreground flex items-center space-x-1">
                        <Icon name="Gift" size={14} className="text-success" />
                        <span>2</span>
                      </span>
                    </div>
                  )}
                  
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Duration</span>
                    <span className="font-medium text-foreground">
                      {eventData?.duration ? `${eventData?.duration} hours` : 'Not set'}
                    </span>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Location</span>
                    <span className="font-medium text-foreground truncate ml-2">
                      {eventData?.location || 'Not set'}
                    </span>
                  </div>
                </div>

                {/* Validation Status */}
                <div className="mt-4 pt-4 border-t border-border">
                  <div className="flex items-center space-x-2">
                    <Icon 
                      name={Object.keys(errors)?.length === 0 && selectedGuests?.length > 0 ? "CheckCircle" : "AlertCircle"} 
                      size={16} 
                      className={Object.keys(errors)?.length === 0 && selectedGuests?.length > 0 ? "text-success" : "text-warning"} 
                    />
                    <span className={`text-sm ${Object.keys(errors)?.length === 0 && selectedGuests?.length > 0 ? "text-success" : "text-warning"}`}>
                      {Object.keys(errors)?.length === 0 && selectedGuests?.length > 0 
                        ? "Ready to save" : "Complete required fields"}
                    </span>
                  </div>
                </div>
              </div>

              {/* Dietary Summary Sidebar */}
              {selectedGuests?.length > 0 && activeTab !== 'dietary' && (
                <DietarySummaryPanel selectedGuests={selectedGuests} />
              )}

              {/* Navigation Actions */}
              <div className="bg-card rounded-lg border border-border p-6">
                <h3 className="text-lg font-semibold text-foreground mb-4">Quick Actions</h3>
                
                <div className="space-y-3">
                  <Button
                    variant="outline"
                    fullWidth
                    onClick={() => navigate('/guest-management')}
                    iconName="UserPlus"
                    iconPosition="left"
                  >
                    Add New Guest
                  </Button>
                  
                  <Button
                    variant="outline"
                    fullWidth
                    onClick={() => setShowPostDinnerFeedback(true)}
                    iconName="MessageSquare"
                    iconPosition="left"
                  >
                    Collect Feedback
                  </Button>
                  
                  <Button
                    variant="outline"
                    fullWidth
                    onClick={() => navigate('/dish-library')}
                    iconName="ChefHat"
                    iconPosition="left"
                  >
                    Browse Recipes
                  </Button>
                  
                  <Button
                    variant="outline"
                    fullWidth
                    onClick={() => navigate('/')}
                    iconName="Home"
                    iconPosition="left"
                  >
                    Back to Dashboard
                  </Button>
                </div>
              </div>
            </div>
          </div>

          {/* Mobile Navigation */}
          <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-card border-t border-border p-4 space-y-3">
            <div className="flex items-center justify-between space-x-3">
              <Button
                variant="outline"
                onClick={() => {
                  const prevTab = getPreviousTab();
                  if (prevTab) setActiveTab(prevTab);
                }}
                disabled={!getPreviousTab()}
                iconName="ChevronLeft"
                iconPosition="left"
              >
                Previous
              </Button>
              
              <Button
                variant="default"
                onClick={() => {
                  const nextTab = getNextTab();
                  if (nextTab && canProceedToNext()) {
                    setActiveTab(nextTab);
                  } else if (!nextTab) {
                    handleSaveEvent();
                  }
                }}
                disabled={!canProceedToNext()}
                loading={isLoading}
                iconName={getNextTab() ? "ChevronRight" : "Save"}
                iconPosition="right"
              >
                {getNextTab() ? 'Next' : 'Save Event'}
              </Button>
            </div>
          </div>
        </div>
      </main>

      {/* Post Dinner Feedback Modal */}
      {showPostDinnerFeedback && (
        <PostDinnerFeedback
          eventData={eventData}
          selectedGuests={selectedGuests}
          onSave={handlePostDinnerFeedbackSave}
          onClose={() => setShowPostDinnerFeedback(false)}
        />
      )}

      <FloatingActionButton onAction={handleFloatingAction} />
    </div>
  );
};

export default DinnerPlanning;